import { IRequestRepository } from './IRequestRepository';
import { ApiResponseModel, JSONMap } from 'models/ApiResponseModel';
import type BaseApiProvider from 'providers/BaseApiProvider';
import { injectable, inject } from 'inversify';
import Logger from 'utilities/log';
import {PaginationModel} from 'models/PaginationModel';
import RequestModel, { RequestGetPayloadModel, RequestPayloadModel } from 'models/Request/RequestModel';
import RequestChangeStatusModel, { RequestChangeStatusGetPayloadModel, RequestChangeStatusPayloadModel } from 'models/Request/RequestChangeStatusModel';
import  { RequestFormBuilderValueGetPayloadModel, RequestFormBuilderValuePayloadListModel, RequestFormBuilderValuePayloadModel } from 'models/Request/RequestFormBuilderValueModel';
import FlowFormBuilderDesignModel from 'models/flow/FlowFormBuilderDesignModel';

@injectable()
class RequestRepository implements IRequestRepository {

    @inject(Symbol.for("BaseApiProvider_5081")) private _provider?: BaseApiProvider;

   //#region Flow 
    
   public async GetAllRequests(params: RequestGetPayloadModel): Promise<{
    rows: RequestModel[];
    pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/RequestInfo/GetAllRequests`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get Flows list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => RequestModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get Flows list: ${error}`);
            throw new Error(`Failed to get smslogs list: ${error}`);
        }
    }
    public async GetRequest(params: RequestGetPayloadModel): Promise<RequestModel| null> {
        try {
            const url = `Api/RequestInfo/GetRequest`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get base info');
                return null;
            }
            return RequestModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }  public async SaveRequestInfo(params: RequestPayloadModel): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/RequestAction/SaveRequestInfo`;
                const response = await this._provider!.post(url, params);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to SaveFlowInfo: ${error}`);
                throw new Error(`Failed to SaveFlowInfo: ${error}`);
            }
        }
    
        public async DeleteRequestInfo(Id: number): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/RequestAction/DeleteRequestInfo`;
                const formData = new FormData();
                formData.append('Id', Id.toString());
                const response = await this._provider!.postForm(url, formData);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to DeleteRequestInfo: ${error}`);
                throw new Error(`Failed to DeleteRequestInfo: ${error}`);
            }
        }
    
    //#endregion Flow
    
   //#region RequestChangeStatus 
    
   public async GetAllRequestChangeStatus(params: RequestChangeStatusGetPayloadModel): Promise<{
    rows: RequestChangeStatusModel[];
    pagination: PaginationModel;
    } | null> {
        try {
            const url = `Api/RequestInfo/GetAllRequestChangeStatus`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get RequestChangeStatus list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => RequestChangeStatusModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get RequestChangeStatus list: ${error}`);
            throw new Error(`Failed to get RequestChangeStatus list: ${error}`);
        }
    }
    public async GetRequestChangeStatus(params: RequestChangeStatusGetPayloadModel): Promise<RequestChangeStatusModel| null> {
        try {
            const url = `Api/RequestInfo/GetFlowSetting`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get GetRequestChangeStatus info');
                return null;
            }
            return RequestChangeStatusModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }  public async SaveRequestChangeStatusInfo(params: RequestChangeStatusPayloadModel): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/RequestAction/SaveRequestChangeStatusInfo`;
                const response = await this._provider!.post(url, params);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to SaveRequestChangeStatusInfo: ${error}`);
                throw new Error(`Failed to SaveRequestChangeStatusInfo: ${error}`);
            }
        }
    
        public async DeleteRequestChangeStatusInfo(Id: number): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/RequestAction/DeleteRequestChangeStatusInfo`;
                const formData = new FormData();
                formData.append('Id', Id.toString());
                const response = await this._provider!.postForm(url, formData);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to DeleteRequestChangeStatusInfo: ${error}`);
                throw new Error(`Failed to DeleteRequestChangeStatusInfo: ${error}`);
            }
        }
    
        public async AddRequestChangeStatusFile(Id: number, File1: File): Promise<ApiResponseModel | null> {
                try {
                    const url = `Api/RequestAction/AddRequestChangeStatusFile`;
                    const formData = new FormData();
                    formData.append('Id', Id.toString());
                    formData.append('File1', File1);
                    const response = await this._provider!.postForm(url, formData);
                    const res = ApiResponseModel.fromJson(response);
                    res.data = response["data"];
                    return res;
                } catch (error) {
                    Logger.event('API_RESPONSE_ERROR', `Failed to add account logo: ${error}`);
                    throw new Error(`Failed to add account logo: ${error}`);
                }
            }
        public async AddRequestChangeStatusWithFile(Id: number,RequestId: number,FlowSettingId: number,
            FlowSettingResultId: number,RadPeygirId: string,Description: string, File1: File): Promise<ApiResponseModel | null> {
                try {
                    const url = `Api/RequestAction/AddRequestChangeStatusWithFile`;
                    const formData = new FormData();
                    formData.append('Id', Id.toString());
                    formData.append('RequestId', RequestId.toString());
                    formData.append('FlowSettingId', FlowSettingId.toString());
                    formData.append('FlowSettingResultId', FlowSettingResultId.toString());
                    formData.append('RadPeygirId', RadPeygirId.toString());
                    formData.append('Description', Description.toString());
                    formData.append('File1', File1);
                    const response = await this._provider!.postForm(url, formData);
                    const res = ApiResponseModel.fromJson(response);
                    res.data = response["data"];
                    return res;
                } catch (error) {
                    Logger.event('API_RESPONSE_ERROR', `Failed to add account logo: ${error}`);
                    throw new Error(`Failed to add account logo: ${error}`);
                }
            }
    //#endregion RequestChangeStatus
   
   
   //#region FlowSetting 
    
   public async GetAllRequestFormBuilderValue(params: RequestFormBuilderValueGetPayloadModel): Promise<{
    rows: FlowFormBuilderDesignModel[];
    pagination: PaginationModel;
} | null> {
        try {
            const url = `Api/RequestInfo/GetAllRequestFormBuilderValue`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get RequestFormBuilderValue list');
                return null;
            }

            return {
                rows: response["data"]["rows"].map((js: any) => FlowFormBuilderDesignModel.fromJson(js)),
                pagination: PaginationModel.fromJson(response["data"])
            };
        } catch (error) {
            Logger.event('API_RESPONSE_ERROR', `Failed to get RequestFormBuilderValue list: ${error}`);
            throw new Error(`Failed to get RequestFormBuilderValue list: ${error}`);
        }
    }
    public async GetRequestFormBuilderValue(params: RequestFormBuilderValueGetPayloadModel): Promise<FlowFormBuilderDesignModel| null> {
        try {
            const url = `Api/RequestInfo/GetRequestFormBuilderValue`;
            const response = await this._provider!.post(url, params);
            const res = ApiResponseModel.fromJson(response);

            if (!res.status) {
                Logger.event('API_RESPONSE_ERROR', res.error || 'Failed to get RequestChangeStatus info');
                return null;
            }
            return FlowFormBuilderDesignModel.fromJson(response["data"]);

        } catch (error) {
            if (error instanceof Error) {
                Logger.event('API_RESPONSE_ERROR', `Api failed: ${error.message}`);
                throw new Error(`Api failed: ${error.message}`);
            } else {
                Logger.event('API_RESPONSE_ERROR', 'Api failed: Unknown error');
                throw new Error('Api failed: Unknown error');
            }
        }
    }  public async SaveRequestFormBuilderValueInfo(params: RequestFormBuilderValuePayloadListModel): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/RequestInfo/SaveRequestFormBuilderValueInfo`;
                const response = await this._provider!.post(url, params);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to SaveRequestFormBuilderValueInfo: ${error}`);
                throw new Error(`Failed to SaveRequestFormBuilderValueInfo: ${error}`);
            }
        }
    
        public async DeleteRequestFormBuilderValueInfo(Id: number): Promise<ApiResponseModel | null> {
            try {
                const url = `Api/RequestInfo/DeleteRequestFormBuilderValueInfo`;
                const formData = new FormData();
                formData.append('Id', Id.toString());
                const response = await this._provider!.postForm(url, formData);
                const res = ApiResponseModel.fromJson(response);
                res.data = response["data"];
                return res;
            } catch (error) {
                Logger.event('API_RESPONSE_ERROR', `Failed to DeleteRequestChangeStatusInfo: ${error}`);
                throw new Error(`Failed to DeleteRequestChangeStatusInfo: ${error}`);
            }
        }
    
    //#endregion FlowSetting
   
}

export default RequestRepository;
